import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';

type CatalogCourse = {
  id: number; name: string; code: string | null; descriptionHtml: string | null;
  placesLeft: number | null; needsApproval: boolean; enrolled: boolean;
  prerequisites: { id: number; name: string; met: boolean }[];
  request: { id: number; status: string; reason: string | null } | null;
};
type EnrollmentRequest = { id: number; courseId: number; courseName: string; status: 'pending' | 'approved' | 'rejected' | 'cancelled'; reason: string | null; requestedAt: string; revision: number };
type Cohort = { id: number; name: string; description: string | null };

/**
 * Courses a trainee can join, and the cohort code.
 *
 * The code comes first, because that is how an intake actually arrives: one
 * code in a letter, and everything else follows from it.
 */
export function Catalog() {
  const [courses, setCourses] = useState<CatalogCourse[] | null>(null);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [requests, setRequests] = useState<EnrollmentRequest[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, g, r] = await Promise.all([
        api.get<{ courses: CatalogCourse[] }>('/catalog'),
        api.get<{ cohorts: Cohort[] }>('/catalog/my-cohorts'),
        api.get<{ requests: EnrollmentRequest[] }>('/catalog/requests'),
      ]);
      setCourses(c.courses); setCohorts(g.cohorts); setRequests(r.requests);
    } catch (e) { setError((e as ApiError).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function changeRequest(url: string, key: string, message: string, body?: object) {
    setBusy(key); setError(null);
    try { await api.post(url, body); setNote(message); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  if (error && !courses) return <p className="auth__error">{error}</p>;
  if (!courses) return <p className="muted">Loading…</p>;

  return (
    <>
      <h1 className="h1">Course catalogue</h1>
      {note && <p className="notice">{note}</p>}
      {error && <p className="auth__error">{error}</p>}

      <CohortKey cohorts={cohorts}
        onJoined={(m) => { setNote(m); setError(null); void load(); }}
        onError={setError} />

      {requests.length > 0 && <section className="panel" aria-labelledby="enrollment-request-heading">
        <h2 id="enrollment-request-heading" className="h2">Your enrollment requests</h2>
        <p className="muted small">A request does not reserve a place. An administrator checks availability before approving it.</p>
        <ul className="cardlist">{requests.map(r => <li key={r.id} className="cardlist__item">
          <div className="cardlist__main"><strong>{r.courseName}</strong>
            <span>Status: {r.status === 'pending' ? 'Awaiting approval' : r.status}</span>
            {r.reason && <span>{r.reason}</span>}
            {r.status === 'pending' && <button className="btn btn--ghost" disabled={busy !== null}
              onClick={() => void changeRequest('/catalog/requests/' + r.id + '/cancel', 'request-' + r.id, 'Enrollment request cancelled.', { revision: r.revision })}>
              {busy === 'request-' + r.id ? 'Cancelling…' : 'Cancel request'}</button>}
          </div>
        </li>)}</ul>
      </section>}

      <h2 className="h2">Courses you can join</h2>
      {courses.length === 0 && <p className="notice">There is nothing open to join at the moment.</p>}

      <ul className="cardlist">
        {courses.map((c) => {
          const blocked = c.prerequisites.filter((p) => !p.met);
          const full = c.placesLeft === 0;
          return (
            <li key={c.id} className="cardlist__item">
              <div className="cardlist__main">
                <strong>
                  {c.name}
                  {c.code && <span className="muted small"> · {c.code}</span>}
                </strong>

                {c.descriptionHtml && (
                  <span className="muted small">{c.descriptionHtml.replace(/<[^>]*>/g, '').slice(0, 180)}</span>
                )}

                <span className="muted small">
                  {c.placesLeft === null
                    ? 'No limit on places'
                    : `${c.placesLeft} ${c.placesLeft === 1 ? 'place' : 'places'} left`}
                </span>

                {blocked.length > 0 && (
                  <span className="warn small">
                    Finish {blocked.map((p) => p.name).join(', ')} first.
                  </span>
                )}

                <span className="toolbar">
                  {c.enrolled ? (
                    <Link className="btn btn--ghost" to={`/courses/${c.id}`}>You are on this — open it</Link>
                  ) : c.needsApproval ? (
                    c.request?.status === 'pending'
                      ? <span className="muted small">Awaiting approval</span>
                      : <button className="btn btn--primary" disabled={busy !== null || blocked.length > 0}
                          onClick={() => void changeRequest('/catalog/courses/' + c.id + '/request', 'course-' + c.id, 'Your enrollment request has been sent for approval.')}>
                          {busy === 'course-' + c.id ? 'Requesting…' : c.request?.status === 'rejected' || c.request?.status === 'cancelled' ? 'Request again' : 'Request enrollment'}
                        </button>
                  ) : (
                    <button className="btn btn--primary"
                      disabled={blocked.length > 0 || full}
                      onClick={async () => {
                        try {
                          await api.post(`/catalog/courses/${c.id}/join`);
                          setNote(`You are on ${c.name}.`); setError(null);
                          await load();
                        } catch (e) { setError((e as ApiError).message); }
                      }}>
                      {full ? 'Full' : 'Join this course'}
                    </button>
                  )}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function CohortKey({ cohorts, onJoined, onError }: {
  cohorts: Cohort[];
  onJoined: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <section className="panel">
      <h2 className="h2">Have a cohort code?</h2>
      <p className="muted small">
        If you were given a code when you were recruited, type it here and you will be put on
        everything your intake studies.
      </p>

      <div className="form">
        <label><span>Cohort code</span>
          <input value={key} placeholder="ACEH-2026" autoCapitalize="characters"
            onChange={(e) => setKey(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && key.trim()) void redeem(); }} />
        </label>
        <button className="btn btn--primary" disabled={busy || !key.trim()} onClick={() => void redeem()}>
          {busy ? 'Checking…' : 'Join'}
        </button>
      </div>

      {cohorts.length > 0 && (
        <p className="muted small">
          You are in {cohorts.map((c) => c.name).join(', ')}.
        </p>
      )}
    </section>
  );

  async function redeem() {
    setBusy(true);
    try {
      const res = await api.post<{
        cohort: string; already: boolean; joined: number;
        skipped: { name: string; reason: string }[];
      }>('/catalog/key', { key: key.trim() });

      setKey('');
      const parts = [
        res.already ? `You were already in ${res.cohort}.` : `You have joined ${res.cohort}.`,
        res.joined > 0 ? `${res.joined} ${res.joined === 1 ? 'course has' : 'courses have'} been added.` : '',
        res.skipped.length ? `Not added: ${res.skipped.map((x) => `${x.name} (${x.reason})`).join(', ')}.` : '',
      ].filter(Boolean);
      onJoined(parts.join(' '));
    } catch (e) { onError((e as ApiError).message); }
    finally { setBusy(false); }
  }
}
